/**
 * ч.843 (рішення власника): віджети-монітори головної сторінки.
 *
 * Спільний патерн: останні 5 рядків + посилання «усі →» на повну сторінку.
 * Дані — з наявних endpoint'ів моніторів (нічого не дублюємо).
 */
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { postingMonitor } from '@/api/postingMonitor';
import { signControl } from '@/api/signControl';
import { bankPayments, bankReceipts } from '@/api/bankDocs';
import { docTableName } from '@/utils/moduleNames';
import { useOrgFilter } from '@/store/defaults';
import DashboardWidget from '../DashboardWidget';

// ч.844 (рішення власника): у віджетах — 3 останні рядки, не 5.
const LIMIT = 3;
// Банк має власні віджети (збережені / нерозпізнані) — у «непроведених» його не показуємо.
const BANK_TYPES = new Set(['doc.bank_payments', 'doc.bank_receipts']);

const fmtAmount = (v: number | string | null | undefined) =>
  v == null ? '' : Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (v: string | null | undefined) =>
  v ? new Date(v).toLocaleDateString('uk-UA') : '';

/** Рядок списку віджета: дата · назва · сума, клік → документ. */
function Row({ left, mid, right, onClick, title }: {
  left: string; mid: string; right?: string; onClick?: () => void; title?: string;
}) {
  return (
    <div
      onClick={onClick}
      title={title}
      style={{
        display: 'flex', gap: 8, alignItems: 'baseline', padding: '3px 0',
        borderBottom: '1px solid var(--border-soft, #f1f5f9)',
        cursor: onClick ? 'pointer' : undefined, fontSize: '0.85rem',
      }}
    >
      <span style={{ color: 'var(--muted)', whiteSpace: 'nowrap', minWidth: 72 }}>{left}</span>
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{mid}</span>
      {right && <span style={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{right}</span>}
    </div>
  );
}

function MoreLink({ to, label }: { to: string; label: string }) {
  return (
    <Link to={to} style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>{label}</Link>
  );
}

/** Монітор проведень: останні 5 непроведених. */
export function UnpostedWidget() {
  const navigate = useNavigate();
  const [orgId] = useOrgFilter();
  const q = useQuery({
    // Беремо з запасом і відкидаємо банк на клієнті (endpoint фільтрує по ОДНОМУ типу).
    queryKey: ['dash-unposted', orgId],
    queryFn: () => postingMonitor.unposted({
      organization_id: orgId || undefined, limit: 50, sort_by: 'date', sort_dir: 'desc' }),
    staleTime: 60_000,
  });
  const all = (q.data?.items ?? []).filter((r) => !BANK_TYPES.has(r.document_type));
  const items = all.slice(0, LIMIT);
  return (
    <DashboardWidget
      title="Непроведені"
      loading={q.isLoading}
      isEmpty={!q.isLoading && items.length === 0}
      emptyMessage="Непроведених документів немає"
      actions={<MoreLink to="/monitor/posting" label="усі →" />}
    >
      {items.map((r) => (
        <Row
          key={`${r.document_type}:${r.document_id}`}
          left={fmtDate(r.date)}
          mid={`${docTableName(r.document_type)} № ${r.number ?? '—'}${r.contractor_name ? ` · ${r.contractor_name}` : ''}`}
          right={fmtAmount(r.amount)}
          title={r.organization_name ?? undefined}
          onClick={() => navigate('/monitor/posting')}
        />
      ))}
    </DashboardWidget>
  );
}

/** Монітор підписання: останні 5 непідписаних. */
export function UnsignedWidget() {
  const navigate = useNavigate();
  const [orgId] = useOrgFilter();
  const q = useQuery({
    queryKey: ['dash-unsigned', orgId],
    queryFn: () => signControl.unsigned({
      organization_id: orgId || undefined, limit: LIMIT, sort_by: 'date', sort_dir: 'desc' }),
    staleTime: 60_000,
  });
  const items = q.data?.items ?? [];
  return (
    <DashboardWidget
      title="Непідписані"
      loading={q.isLoading}
      isEmpty={!q.isLoading && items.length === 0}
      emptyMessage="Непідписаних документів немає"
      actions={<MoreLink to="/edo/sign-control" label="усі →" />}
    >
      {items.map((r) => (
        <Row
          key={`${r.document_type}:${r.document_id}`}
          left={fmtDate(r.date)}
          mid={`${docTableName(r.document_type)} № ${r.number ?? '—'}${r.contractor_name ? ` · ${r.contractor_name}` : ''}`}
          right={fmtAmount(r.amount)}
          title={r.organization_name ?? undefined}
          onClick={() => navigate('/edo/sign-control')}
        />
      ))}
    </DashboardWidget>
  );
}

/** Монітор банку: останні 5 надходжень / списань. */
export function BankFlowWidget({ kind }: { kind: 'receipts' | 'payments' }) {
  const navigate = useNavigate();
  const isIn = kind === 'receipts';
  const [orgId] = useOrgFilter();
  const q = useQuery({
    queryKey: ['dash-bank', kind, orgId],
    queryFn: () => {
      const p = { organization_id: orgId || undefined, posted: true, limit: LIMIT,
        sort_by: 'document_date', sort_dir: 'desc' as const };
      return isIn ? bankReceipts.list(p) : bankPayments.list(p);
    },
    staleTime: 60_000,
  });
  const items = q.data?.items ?? [];
  return (
    <DashboardWidget
      title={isIn ? 'Надходження на рахунок' : 'Списання з рахунку'}
      loading={q.isLoading}
      isEmpty={!q.isLoading && items.length === 0}
      emptyMessage="Документів немає"
      actions={<MoreLink to={isIn ? '/bank-receipts' : '/bank-payments'} label="усі →" />}
    >
      {items.map((r) => {
        const party = isIn
          ? ((r as { payer_display?: string | null }).payer_display ?? '')
          : ((r as { recipient_display?: string | null }).recipient_display ?? '');
        return (
          <Row
            key={r.id}
            left={fmtDate(r.document_date)}
            mid={`№ ${r.document_number}${party ? ` · ${party}` : ''}`}
            right={fmtAmount(r.amount)}
            onClick={() => navigate(`${isIn ? '/bank-receipts' : '/bank-payments'}/${r.id}`)}
          />
        );
      })}
    </DashboardWidget>
  );
}

export const _internal = { fmtAmount, fmtDate };


/** ч.844: збережені платіжки (чернетки, ще не відправлені в банк) — останні 3. */
export function SavedPaymentsWidget() {
  const navigate = useNavigate();
  const [orgId] = useOrgFilter();
  const q = useQuery({
    queryKey: ['dash-saved-payments', orgId],
    queryFn: () => bankPayments.list({
      organization_id: orgId || undefined, posted: false, needs_review: false, sent: false,
      limit: LIMIT, sort_by: 'document_date', sort_dir: 'desc' }),
    staleTime: 60_000,
  });
  const items = q.data?.items ?? [];
  return (
    <DashboardWidget
      title="Збережені платіжки"
      loading={q.isLoading}
      isEmpty={!q.isLoading && items.length === 0}
      emptyMessage="Збережених платіжок немає"
      actions={<MoreLink to="/bank-payments?tab=saved" label="усі →" />}
    >
      {items.map((r) => (
        <Row
          key={r.id}
          left={fmtDate(r.document_date)}
          mid={`№ ${r.document_number}${r.recipient_display ? ` · ${r.recipient_display}` : ''}`}
          right={fmtAmount(r.amount)}
          onClick={() => navigate(`/bank-payments/${r.id}?from=saved`)}
        />
      ))}
    </DashboardWidget>
  );
}

/** ч.844: нерозпізнані з виписки — надходження або списання, останні 3. */
export function UnrecognizedWidget({ kind }: { kind: 'receipts' | 'payments' }) {
  const navigate = useNavigate();
  const [orgId] = useOrgFilter();
  const isIn = kind === 'receipts';
  const q = useQuery({
    queryKey: ['dash-unrecognized', kind, orgId],
    queryFn: () => {
      // Надходження: «нерозпізнані» = всі чернетки; списання: needs_review.
      const base = { organization_id: orgId || undefined, posted: false, limit: LIMIT,
        sort_by: 'document_date', sort_dir: 'desc' as const };
      return isIn ? bankReceipts.list(base) : bankPayments.list({ ...base, needs_review: true });
    },
    staleTime: 60_000,
  });
  const items = q.data?.items ?? [];
  return (
    <DashboardWidget
      title={isIn ? 'Нерозпізнані надходження' : 'Нерозпізнані списання'}
      loading={q.isLoading}
      isEmpty={!q.isLoading && items.length === 0}
      emptyMessage="Нерозпізнаних немає"
      actions={<MoreLink to={`${isIn ? '/bank-receipts' : '/bank-payments'}?tab=review`}
        label="усі →" />}
    >
      {items.map((r) => {
        const party = isIn
          ? ((r as { payer_display?: string | null }).payer_display ?? '')
          : ((r as { recipient_display?: string | null }).recipient_display ?? '');
        return (
          <Row
            key={r.id}
            left={fmtDate(r.document_date)}
            mid={`№ ${r.document_number}${party ? ` · ${party}` : ''}`}
            right={fmtAmount(r.amount)}
            onClick={() => navigate(`${isIn ? '/bank-receipts' : '/bank-payments'}?tab=review`)}
          />
        );
      })}
    </DashboardWidget>
  );
}
